import React, { useEffect } from 'react';
import moment from 'moment';
import { useStore } from '../../../../../../stores/root';

// assets

// services
import OrderService from '../../../../../../services/orders/order';

// props
import { IOrderContainerGoodsResourceShortProps } from '../../../../../../props/orders/containers/goods';
import { IGoodsResourceShortProps } from '../../../../../../props/goods/goods';
import { IOrderResourceProps } from '../../../../../../props/orders/order';

// components
import { ActionButton, Checkbox, DefaultButton, Dialog, DialogFooter, DialogType, IColumn, IconButton, Link, MessageBar, MessageBarType, Panel, PanelType, PrimaryButton, SelectionMode, ShimmeredDetailsList, Stack, TagPicker, TextField } from '@fluentui/react';
import Text from './../../../../../typography/text';
import GoodsService from '../../../../../../services/goods/goods';
import ErrorService from '../../../../../../services/general/error';
import { faCheck, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';
import { IOrderContainerResourceShortProps } from '../../../../../../props/orders/containers';
import GeneralService from '../../../../../../services/general';
import Label from '../../../../../typography/label';
import LoadingComponent from '../../../../../feedbacks/loading';
import GoodsForm from '../../../../../goods/general/form';
import OrderContainerService from '../../../../../../services/orders/orderContainer';
import { IOrderGoodsResourceShortProps } from '../../../../../../props/orders/orderGoods';
import TallySheetScanModal, { ExtractedTallyItem } from './TallySheetScanModal';
import SuratJalanGoodsScanModal, { ExtractedSuratJalanInfo } from './SuratJalanGoodsScanModal';
import PackingListStorageService, { ISavedPackingListHistoryData, ISavedPackingListItem } from '../../../../../../services/orders/packingListStorage';
import PackingListHistoryModal from './historyModal';

interface TOrderContainerGoodsFormProps {
    orderId: string;
    container: IOrderContainerResourceShortProps;
    goods?: IOrderContainerGoodsResourceShortProps[];
    onDismissed(refresh?: boolean): void;
}

type FormDataProps = {
    id: string;
    goods?: IGoodsResourceShortProps;
    supplier?: string;
    deliveryDate?: string;
    quantityAmount: string;
    quantityUnit: string;
    length: string;
    width: string;
    height: string;
    weightAmount: string;
    weightUnit: string;
    useTotalVolume: boolean;
    orderNumber: number;
}

const OrderContainerGoodsForm: React.FC<TOrderContainerGoodsFormProps> = (props: TOrderContainerGoodsFormProps) => {
    const { banner } = useStore();
    const { container } = props;
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [goods, setGoods] = React.useState<Partial<IOrderContainerGoodsResourceShortProps>[]>(props.goods || []);
    const [data, setData] = React.useState<FormDataProps[]>([]);
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();
    const [editingGoodsId, setEditingGoodsId] = React.useState<string | undefined>();
    const [order, setOrder] = React.useState<IOrderResourceProps | undefined>();
    const [rowCountInput, setRowCountInput] = React.useState<string>('5');
    const [isTallyScanOpen, setIsTallyScanOpen] = React.useState<boolean>(false);
    const [isSuratJalanScanOpen, setIsSuratJalanScanOpen] = React.useState<boolean>(false);

    const [selectedIds, setSelectedIds] = React.useState<string[]>([]);

    const [isDirty, setIsDirty] = React.useState<boolean>(false);
    const [showUnsavedDialog, setShowUnsavedDialog] = React.useState<boolean>(false);

    // Packing list history & cross-order states
    const [isHistoryModalOpen, setIsHistoryModalOpen] = React.useState<boolean>(false);
    const [savedHistoryData, setSavedHistoryData] = React.useState<ISavedPackingListHistoryData | null>(PackingListStorageService.getLatest());
    const [hasSavedHistory, setHasSavedHistory] = React.useState<boolean>(false);
    const [showClearAllDialog, setShowClearAllDialog] = React.useState<boolean>(false);

    const handleAttemptDismiss = (refresh: boolean = false) => {
        if (isDirty) {
            setShowUnsavedDialog(true);
        } else {
            props.onDismissed(refresh);
        }
    };


    const handleSelectAll = (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        if (checked) {
            setSelectedIds(data.map(d => d.id));
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectRow = (id: string, checked?: boolean) => {
        if (checked) {
            setSelectedIds(prev => [...prev, id]);
        } else {
            setSelectedIds(prev => prev.filter(i => i !== id));
        }
    };

    const handleBulkDelete = () => {
        if (selectedIds.length === 0) return;
        const remaining = data.filter(d => !selectedIds.includes(d.id));

        if (remaining.length === 0) {
            remaining.push({
                id: GeneralService.guid(),
                supplier: "",
                quantityAmount: "",
                quantityUnit: "",
                length: "",
                width: "",
                height: "",
                weightAmount: "",
                weightUnit: "",
                useTotalVolume: false,
                orderNumber: 1
            });
        }

        const reindexed = remaining.map((d, idx) => ({ ...d, orderNumber: idx + 1 }));
        setData([...reindexed]);
        setIsDirty(true);
        const deletedCount = selectedIds.length;
        setSelectedIds([]);

        banner.add({
            key: 'bulk_delete_container_goods_success',
            variant: 'success',
            icon: faCheck,
            text: `${deletedCount} baris berhasil dihapus! Klik 'Save' di bawah untuk menyimpan perubahan.`
        });
    };


    const handleApplyTallyItems = async (extractedItems: ExtractedTallyItem[], containerInfo?: any) => {
        if (!extractedItems || extractedItems.length === 0) return;

        try {
            setSubmitting(true);
            let currentOrder = order;
            if (!currentOrder && props.orderId) {
                try {
                    currentOrder = await OrderService.get(props.orderId);
                    setOrder(currentOrder);
                } catch (e) {
                    console.error("Order fetch error in handleApplyTallyItems:", e);
                }
            }

            const newFormDataRows: FormDataProps[] = [];
            let filledData = data.filter((d) => d.goods);
            let maxOrder = filledData.length > 0 ? Math.max(...filledData.map((d) => d.orderNumber)) : 0;

            for (let i = 0; i < extractedItems.length; i++) {
                const item = extractedItems[i];
                let goodsRecord: IGoodsResourceShortProps | undefined = item.matchedGoods;

                // 1. Try to match existing master goods by name
                if (!goodsRecord && item.jenisBarang && item.jenisBarang.trim() !== '') {
                    try {
                        const searchRes: any = await GoodsService.retrieve.get(`search=${encodeURIComponent(item.jenisBarang.trim())}`);
                        const itemsArr: any[] = searchRes?.data || (Array.isArray(searchRes) ? searchRes : []);
                        if (Array.isArray(itemsArr) && itemsArr.length > 0) {
                            const exact = itemsArr.find((g: any) => g.name?.toLowerCase().trim() === item.jenisBarang.toLowerCase().trim());
                            goodsRecord = exact || itemsArr[0];
                        }
                    } catch (e) {
                        console.error("Search existing goods error:", e);
                    }
                }

                // 2. If not found in DB, auto-create master Goods in DB with all Create Goods fields
                if (!goodsRecord && item.jenisBarang && item.jenisBarang.trim() !== '') {
                    try {
                        const fd = new FormData();
                        fd.append('name', item.jenisBarang.trim().toUpperCase());

                        const companyId = item.company?.id || currentOrder?.company?.id;
                        const userId = item.customer?.id || currentOrder?.customer?.id;
                        if (companyId) fd.append('companyId', companyId);
                        if (userId) fd.append('userId', userId);

                        if (item.quantityAmount) fd.append('quantityAmount', item.quantityAmount);
                        if (item.quantityUnit) fd.append('quantityUnit', item.quantityUnit);
                        if (item.weightAmount) fd.append('weightAmount', item.weightAmount);
                        if (item.weightUnit) fd.append('weightUnit', item.weightUnit);

                        // Convert length/width/height to cm if unit is m or mm
                        let finalLength = item.length || '';
                        let finalWidth = item.width || '';
                        let finalHeight = item.height || '';
                        const dimUnit = item.dimensionUnit || 'cm';
                        if (dimUnit === 'm') {
                            if (finalLength && !isNaN(Number(finalLength))) finalLength = (Number(finalLength) * 100).toString();
                            if (finalWidth && !isNaN(Number(finalWidth))) finalWidth = (Number(finalWidth) * 100).toString();
                            if (finalHeight && !isNaN(Number(finalHeight))) finalHeight = (Number(finalHeight) * 100).toString();
                        } else if (dimUnit === 'mm') {
                            if (finalLength && !isNaN(Number(finalLength))) finalLength = (Number(finalLength) / 10).toString();
                            if (finalWidth && !isNaN(Number(finalWidth))) finalWidth = (Number(finalWidth) / 10).toString();
                            if (finalHeight && !isNaN(Number(finalHeight))) finalHeight = (Number(finalHeight) / 10).toString();
                        }

                        if (finalLength) fd.append('length', finalLength);
                        if (finalWidth) fd.append('width', finalWidth);
                        if (finalHeight) fd.append('height', finalHeight);

                        if (item.warehouse?.id) fd.append('warehouseId', item.warehouse.id);
                        if (item.locationDetails) fd.append('locationDetails', item.locationDetails);
                        if (item.supplier) fd.append('supplier', item.supplier.toUpperCase());
                        if (item.deliveryDate) fd.append('deliveryDate', item.deliveryDate);
                        if (item.notes) fd.append('notes', item.notes);

                        const createdRes: any = await GoodsService.create(fd);
                        goodsRecord = createdRes?.data || createdRes;
                    } catch (e) {
                        console.error("Auto create master goods error:", e);
                    }
                }

                // 3. Fallback: ensure goodsRecord is defined so the row is guaranteed to be added
                if (!goodsRecord && item.jenisBarang && item.jenisBarang.trim() !== '') {
                    goodsRecord = {
                        id: GeneralService.guid(),
                        name: item.jenisBarang.trim(),
                        userId: item.customer?.id || order?.customer?.id || "",
                        user: (item.customer || order?.customer || {}) as any,
                        company: item.company || order?.company,
                        remainingQuantity: item.quantityAmount ? Number(item.quantityAmount) : 0,
                        quantityAmount: item.quantityAmount ? Number(item.quantityAmount) : undefined,
                        quantityUnit: item.quantityUnit || undefined,
                        weightAmount: item.weightAmount ? Number(item.weightAmount) : undefined,
                        weightUnit: item.weightUnit || undefined,
                        length: item.length ? Number(item.length) : undefined,
                        width: item.width ? Number(item.width) : undefined,
                        height: item.height ? Number(item.height) : undefined,
                        supplier: item.supplier || undefined,
                        deliveryDate: item.deliveryDate || undefined,
                        warehouse: item.warehouse || undefined,
                        locationDetails: item.locationDetails || undefined,
                        notes: item.notes || undefined
                    };
                }

                if (goodsRecord) {
                    maxOrder += 1;
                    newFormDataRows.push({
                        id: GeneralService.guid(),
                        goods: goodsRecord,
                        deliveryDate: item.deliveryDate || (goodsRecord as any).deliveryDate || "",
                        supplier: item.supplier || goodsRecord.supplier || "",
                        quantityAmount: item.quantityAmount || (goodsRecord.quantityAmount ? String(goodsRecord.quantityAmount) : "1"),
                        quantityUnit: item.quantityUnit || goodsRecord.quantityUnit || "CTN",
                        length: item.length || (goodsRecord.length ? String(goodsRecord.length) : ""),
                        width: item.width || (goodsRecord.width ? String(goodsRecord.width) : ""),
                        height: item.height || (goodsRecord.height ? String(goodsRecord.height) : ""),
                        weightAmount: item.weightAmount || (goodsRecord.weightAmount ? String(goodsRecord.weightAmount) : ""),
                        weightUnit: item.weightUnit || goodsRecord.weightUnit || "kg",
                        useTotalVolume: false,
                        orderNumber: maxOrder
                    });
                }
            }

            const combined = [...filledData, ...newFormDataRows];
            const reindexed = combined.map((d, idx) => ({ ...d, orderNumber: idx + 1 }));

            reindexed.push({
                id: GeneralService.guid(),
                supplier: "",
                quantityAmount: "",
                quantityUnit: "",
                length: "",
                width: "",
                height: "",
                weightAmount: "",
                weightUnit: "",
                useTotalVolume: false,
                orderNumber: reindexed.length + 1
            });

            setData([...reindexed]);
            setIsDirty(true);
            setSubmitting(false);

            // Save snapshot to history
            const filledAfterTally = reindexed.filter(d => d.goods);
            if (filledAfterTally.length > 0) {
                PackingListStorageService.save({
                    source: 'tally_scan',
                    orderId: props.orderId,
                    orderNumber: order?.orderNumber,
                    containerId: props.container.id,
                    containerNumber: props.container.containerNumber,
                    companyName: order?.company?.name,
                    customerName: order?.customer?.name,
                    goods: filledAfterTally
                });
            }

            banner.add({
                key: 'tally_sheet_apply_success',
                variant: 'success',
                icon: faCheck,
                text: `${newFormDataRows.length} barang dari Tally Sheet berhasil ditambahkan ke Packing List!`
            });
        } catch (e) {
            setSubmitting(false);
            banner.add({
                key: 'tally_sheet_apply_error',
                variant: 'error',
                icon: faXmarkCircle,
                text: 'Gagal menambahkan barang Tally Sheet. Error: ' + ErrorService.getMessage(e)
            });
        }
    };

    const handleApplySuratJalanItems = async (extractedItems: ExtractedTallyItem[], suratJalanInfo?: ExtractedSuratJalanInfo) => {
        if (!extractedItems || extractedItems.length === 0) return;

        try {
            setSubmitting(true);
            let currentOrder = order;
            if (!currentOrder && props.orderId) {
                try {
                    currentOrder = await OrderService.get(props.orderId);
                    setOrder(currentOrder);
                } catch (e) {
                    console.error("Order fetch error in handleApplySuratJalanItems:", e);
                }
            }

            const newFormDataRows: FormDataProps[] = [];
            let filledData = data.filter((d) => d.goods);
            let maxOrder = filledData.length > 0 ? Math.max(...filledData.map((d) => d.orderNumber)) : 0;

            const defaultSupplier = suratJalanInfo?.sender?.companyName || suratJalanInfo?.sender?.contactPerson || "";
            let defaultDeliveryDate = "";
            if (suratJalanInfo?.date && suratJalanInfo.date.trim() !== '') {
                const parsed = moment(suratJalanInfo.date.trim(), ['D.M.YY', 'D.M.YYYY', 'D-M-YY', 'D-M-YYYY', 'D/M/YY', 'D/M/YYYY', 'YYYY-MM-DD', 'YYYY/MM/DD'], true);
                if (parsed.isValid()) {
                    defaultDeliveryDate = parsed.toISOString();
                }
            }

            for (let i = 0; i < extractedItems.length; i++) {
                const item = extractedItems[i];
                let goodsRecord: IGoodsResourceShortProps | undefined = item.matchedGoods;

                // 1. Try to match existing master goods by name
                if (!goodsRecord && item.jenisBarang && item.jenisBarang.trim() !== '') {
                    try {
                        const searchRes: any = await GoodsService.retrieve.get(`search=${encodeURIComponent(item.jenisBarang.trim())}`);
                        const itemsArr: any[] = searchRes?.data || (Array.isArray(searchRes) ? searchRes : []);
                        if (Array.isArray(itemsArr) && itemsArr.length > 0) {
                            const exact = itemsArr.find((g: any) => g.name?.toLowerCase().trim() === item.jenisBarang.toLowerCase().trim());
                            goodsRecord = exact || itemsArr[0];
                        }
                    } catch (e) {
                        console.error("Search existing goods error:", e);
                    }
                }

                const itemSupplier = item.supplier || defaultSupplier;
                const itemDeliveryDate = item.deliveryDate || defaultDeliveryDate;

                // 2. If not found in DB, auto-create master Goods in DB with all Create Goods fields
                if (!goodsRecord && item.jenisBarang && item.jenisBarang.trim() !== '') {
                    try {
                        const fd = new FormData();
                        fd.append('name', item.jenisBarang.trim().toUpperCase());

                        const companyId = item.company?.id || currentOrder?.company?.id;
                        const userId = item.customer?.id || currentOrder?.customer?.id;
                        if (companyId) fd.append('companyId', companyId);
                        if (userId) fd.append('userId', userId);

                        if (item.quantityAmount) fd.append('quantityAmount', item.quantityAmount);
                        if (item.quantityUnit) fd.append('quantityUnit', item.quantityUnit);
                        if (item.weightAmount) fd.append('weightAmount', item.weightAmount);
                        if (item.weightUnit) fd.append('weightUnit', item.weightUnit);

                        let finalLength = item.length || '';
                        let finalWidth = item.width || '';
                        let finalHeight = item.height || '';
                        const dimUnit = item.dimensionUnit || 'cm';
                        if (dimUnit === 'm') {
                            if (finalLength && !isNaN(Number(finalLength))) finalLength = (Number(finalLength) * 100).toString();
                            if (finalWidth && !isNaN(Number(finalWidth))) finalWidth = (Number(finalWidth) * 100).toString();
                            if (finalHeight && !isNaN(Number(finalHeight))) finalHeight = (Number(finalHeight) * 100).toString();
                        } else if (dimUnit === 'mm') {
                            if (finalLength && !isNaN(Number(finalLength))) finalLength = (Number(finalLength) / 10).toString();
                            if (finalWidth && !isNaN(Number(finalWidth))) finalWidth = (Number(finalWidth) / 10).toString();
                            if (finalHeight && !isNaN(Number(finalHeight))) finalHeight = (Number(finalHeight) / 10).toString();
                        }

                        if (finalLength) fd.append('length', finalLength);
                        if (finalWidth) fd.append('width', finalWidth);
                        if (finalHeight) fd.append('height', finalHeight);

                        if (item.warehouse?.id) fd.append('warehouseId', item.warehouse.id);
                        if (item.locationDetails) fd.append('locationDetails', item.locationDetails);
                        if (itemSupplier) fd.append('supplier', itemSupplier.toUpperCase());
                        if (itemDeliveryDate) fd.append('deliveryDate', itemDeliveryDate);
                        if (item.notes) fd.append('notes', item.notes);

                        const createdRes: any = await GoodsService.create(fd);
                        goodsRecord = createdRes?.data || createdRes;
                    } catch (e) {
                        console.error("Auto create master goods error:", e);
                    }
                }

                // 3. Fallback: ensure goodsRecord is defined so the row is guaranteed to be added
                if (!goodsRecord && item.jenisBarang && item.jenisBarang.trim() !== '') {
                    goodsRecord = {
                        id: GeneralService.guid(),
                        name: item.jenisBarang.trim(),
                        userId: item.customer?.id || currentOrder?.customer?.id || "",
                        user: (item.customer || currentOrder?.customer || {}) as any,
                        company: item.company || currentOrder?.company,
                        remainingQuantity: item.quantityAmount ? Number(item.quantityAmount) : 0,
                        quantityAmount: item.quantityAmount ? Number(item.quantityAmount) : undefined,
                        quantityUnit: item.quantityUnit || undefined,
                        weightAmount: item.weightAmount ? Number(item.weightAmount) : undefined,
                        weightUnit: item.weightUnit || undefined,
                        length: item.length ? Number(item.length) : undefined,
                        width: item.width ? Number(item.width) : undefined,
                        height: item.height ? Number(item.height) : undefined,
                        supplier: itemSupplier || undefined,
                        deliveryDate: itemDeliveryDate || undefined,
                        warehouse: item.warehouse || undefined,
                        locationDetails: item.locationDetails || undefined,
                        notes: item.notes || undefined
                    };
                }

                if (goodsRecord) {
                    maxOrder += 1;
                    newFormDataRows.push({
                        id: GeneralService.guid(),
                        goods: goodsRecord,
                        deliveryDate: itemDeliveryDate || (goodsRecord as any).deliveryDate || "",
                        supplier: itemSupplier || goodsRecord.supplier || "",
                        quantityAmount: item.quantityAmount || (goodsRecord.quantityAmount ? String(goodsRecord.quantityAmount) : "1"),
                        quantityUnit: item.quantityUnit || goodsRecord.quantityUnit || "CTN",
                        length: item.length || (goodsRecord.length ? String(goodsRecord.length) : ""),
                        width: item.width || (goodsRecord.width ? String(goodsRecord.width) : ""),
                        height: item.height || (goodsRecord.height ? String(goodsRecord.height) : ""),
                        weightAmount: item.weightAmount || (goodsRecord.weightAmount ? String(goodsRecord.weightAmount) : ""),
                        weightUnit: item.weightUnit || goodsRecord.weightUnit || "kg",
                        useTotalVolume: false,
                        orderNumber: maxOrder
                    });
                }
            }

            if (newFormDataRows.length === 0) {
                setSubmitting(false);
                return;
            }

            const currentFilledRows = data.filter((d) => d.goods);
            const combinedRows = [...currentFilledRows, ...newFormDataRows];
            const reindexed = combinedRows.map((d, idx) => ({ ...d, orderNumber: idx + 1 }));

            // Add placeholder row at the end
            reindexed.push({
                id: GeneralService.guid(),
                supplier: defaultSupplier,
                quantityAmount: "",
                quantityUnit: "",
                length: "",
                width: "",
                height: "",
                weightAmount: "",
                weightUnit: "",
                useTotalVolume: false,
                orderNumber: reindexed.length + 1
            });

            setData([...reindexed]);
            setIsDirty(true);
            setSubmitting(false);

            // Save snapshot to history
            const filledAfterSJ = reindexed.filter(d => d.goods);
            if (filledAfterSJ.length > 0) {
                PackingListStorageService.save({
                    source: 'tally_scan',
                    orderId: props.orderId,
                    orderNumber: order?.orderNumber,
                    containerId: props.container.id,
                    containerNumber: props.container.containerNumber,
                    companyName: order?.company?.name,
                    customerName: order?.customer?.name,
                    goods: filledAfterSJ
                });
            }

            banner.add({
                key: 'surat_jalan_apply_success',
                variant: 'success',
                icon: faCheck,
                text: `${newFormDataRows.length} barang dari Surat Jalan ${suratJalanInfo?.deliveryNoteNumber ? '#' + suratJalanInfo.deliveryNoteNumber : ''} berhasil ditambahkan ke Daftar Barang!`
            });
        } catch (e) {
            setSubmitting(false);
            banner.add({
                key: 'surat_jalan_apply_error',
                variant: 'error',
                icon: faXmarkCircle,
                text: 'Gagal menambahkan barang Surat Jalan. Error: ' + ErrorService.getMessage(e)
            });
        }
    };

    const handleAddMultipleRows = (count: number) => {
        if (isNaN(count) || count <= 0) return;
        const safeCount = Math.min(count, 50);

        const newRows: FormDataProps[] = [];
        const currentMaxOrderNumber = data.length > 0 ? Math.max(...data.map(d => d.orderNumber)) : 0;

        for (let i = 0; i < safeCount; i++) {
            newRows.push({
                id: GeneralService.guid(),
                supplier: "",
                quantityAmount: "",
                quantityUnit: "",
                length: "",
                width: "",
                height: "",
                weightAmount: "",
                weightUnit: "",
                useTotalVolume: false,
                orderNumber: currentMaxOrderNumber + i + 1
            });
        }

        setData((prevData) => {
            let updated = [...prevData, ...newRows];
            return updated.sort((a, b) => a.orderNumber - b.orderNumber);
        });
    };

    const handleClearEmptyRows = () => {
        let filled = data.filter((d) => d.goods);
        if (filled.length === 0) {
            filled = [{
                id: GeneralService.guid(),
                supplier: "",
                quantityAmount: "",
                quantityUnit: "",
                length: "",
                width: "",
                height: "",
                weightAmount: "",
                weightUnit: "",
                useTotalVolume: false,
                orderNumber: 1
            }];
        }
        const reindexed = filled.map((d, idx) => ({ ...d, orderNumber: idx + 1 }));
        setData([...reindexed]);
    };

    useEffect(() => {
        let _data: FormDataProps[] = [];
        _data = (props.goods || []).map((good, idx) => {
            return {
                id: good.id || GeneralService.guid(),
                goods: good.goods,
                supplier: good.supplier || good.goods?.supplier || "",
                quantityAmount: (good.quantityAmount || "") + "",
                quantityUnit: good.quantityUnit || good.goods?.quantityUnit || "",
                length: (good.length || "") + "",
                width: (good.width || "") + "",
                height: (good.height || "") + "",
                weightAmount: (good.weightAmount || "") + "",
                weightUnit: good.weightUnit || good.goods?.weightUnit || "",
                useTotalVolume: good.useTotalVolume || false,
                orderNumber: good.orderNumber || (idx + 1)
            }
        });

        // add placeholder data
        _data = [..._data, {
            id: GeneralService.guid(),
            supplier: "",
            quantityAmount: "",
            quantityUnit: "",
            length: "",
            width: "",
            height: "",
            weightAmount: "",
            weightUnit: "",
            useTotalVolume: false,
            orderNumber: _data.length + 1
        }];

        // sort by order number
        _data = _data.sort((a, b) => a.orderNumber - b.orderNumber);

        setData([..._data]);

        // Check latest history snapshot
        const latestSnapshot = PackingListStorageService.getLatest();
        if (latestSnapshot && latestSnapshot.goods && latestSnapshot.goods.length > 0) {
            const isDifferentContainer = latestSnapshot.containerId !== props.container.id;
            const isCurrentContainerEmpty = (props.goods || []).length === 0;
            if (isDifferentContainer || isCurrentContainerEmpty) {
                setSavedHistoryData(latestSnapshot);
                setHasSavedHistory(true);
            }
        }

        const loadOrder = async () => {
            try {
                const _order = await OrderService.get(props.orderId);
                setOrder(_order);
            } catch (e) {
                console.error(e);
            }
        }
        loadOrder();
    }, []);

    // Auto-draft debounce save when user inputs items
    useEffect(() => {
        const filled = data.filter(d => d.goods);
        if (filled.length < 2) return;

        const timer = setTimeout(() => {
            PackingListStorageService.save({
                source: 'draft',
                orderId: props.orderId,
                orderNumber: order?.orderNumber,
                containerId: props.container.id,
                containerNumber: props.container.containerNumber,
                companyName: order?.company?.name,
                customerName: order?.customer?.name,
                goods: filled
            });
        }, 2500);

        return () => clearTimeout(timer);
    }, [data, order, props.orderId, props.container]);

    const supplierSuggestions = React.useMemo(() => {
        const suggestions = new Set<string>();
        if (order?.customer?.name) {
            suggestions.add(order.customer.name);
        }
        if (order?.company?.name) {
            suggestions.add(order.company.name);
        }
        (props.goods || []).forEach((g) => {
            if (g.goods?.supplier && g.goods.supplier.trim() !== '') {
                suggestions.add(g.goods.supplier.trim());
            }
            if (g.supplier && g.supplier.trim() !== '') {
                suggestions.add(g.supplier.trim());
            }
        });
        data.forEach((d) => {
            if (d.supplier && d.supplier.trim() !== '') {
                suggestions.add(d.supplier.trim());
            }
        });
        return Array.from(suggestions);
    }, [order, props.goods, data]);

    const columns: IColumn[] = [
        {
            key: "select",
            name: "",
            minWidth: 32,
            maxWidth: 32,
            onRenderHeader: () => {
                const isAllSelected = data.length > 0 && selectedIds.length === data.length;
                const isIndeterminate = selectedIds.length > 0 && selectedIds.length < data.length;
                return <Stack styles={{ root: { padding: '6px 0px' } }}>
                    <Checkbox
                        checked={isAllSelected}
                        indeterminate={isIndeterminate}
                        onChange={handleSelectAll}
                        title="Pilih Semua Baris"
                    />
                </Stack>;
            },
            onRender: (item: FormDataProps) => {
                const isChecked = selectedIds.includes(item.id);
                return <Stack styles={{ root: { padding: '10px 0px' } }}>
                    <Checkbox
                        checked={isChecked}
                        onChange={(evt, checked) => handleSelectRow(item.id, checked)}
                    />
                </Stack>;
            }
        },

        {
            key: "sort",
            name: "",
            minWidth: 52,
            maxWidth: 52,
            onRender: (item: FormDataProps, idx?: number) => {
                return <Stack.Item styles={{ root: { padding: '6px 0px' } }}>
                    <Stack.Item className={"detailsListActionRow"}>
                        {item.goods ? <>
                            <ActionButton className={'detailsLisAtctionButton'}
                                iconProps={{ iconName: "ChevronUp" }}
                                styles={{ root: { paddingLeft: '2px !important', paddingRight: '2px !important' } }}
                                disabled={(idx || 0) < 1}
                                onClick={() => {
                                    let _data = data;
                                    const didx = _data.findIndex((d) => d.id === item.id);

                                    _data[didx].orderNumber = _data[didx].orderNumber - 1;
                                    _data[didx - 1].orderNumber = _data[didx - 1].orderNumber + 1;

                                    // sort by order number
                                    _data = _data.sort((a, b) => a.orderNumber - b.orderNumber);

                                    setData([..._data]);
                                }} />
                            <ActionButton className={'detailsListActionButton'}
                                iconProps={{ iconName: "ChevronDown" }}
                                styles={{ root: { paddingLeft: '2px !important', paddingRight: '2px !important' } }}
                                disabled={(idx || 0) >= (data.length - 2)}
                                onClick={() => {
                                    let _data = data;
                                    const didx = _data.findIndex((d) => d.id === item.id);

                                    _data[didx].orderNumber = _data[didx].orderNumber + 1;
                                    _data[didx + 1].orderNumber = _data[didx + 1].orderNumber - 1;

                                    // sort by order number
                                    _data = _data.sort((a, b) => a.orderNumber - b.orderNumber);

                                    setData([..._data]);
                                }} />
                        </> : null}
                    </Stack.Item>
                </Stack.Item>;
            }
        },
        {
            key: "idx",
            name: "No.",
            minWidth: 30,
            maxWidth: 30,
            onRender: (item: FormDataProps, idx?: number) => {
                return <Stack styles={{ root: { padding: '10px 0px' } }}>
                    <Text>{(idx || 0) + 1}</Text>
                </Stack>
            }
        },
        {
            key: "goods",
            name: "Goods",
            minWidth: 180,
            onRender: (item: FormDataProps) => {
                const selectedItems = item.goods ? [{ key: item.goods.id || item.goods.name, name: item.goods.name, metadata: item.goods }] : [];
                return <Stack styles={{ root: { padding: '4px 0px' } }} tokens={{ childrenGap: 3 }}>
                    <TagPicker
                        selectedItems={selectedItems}
                        removeButtonAriaLabel='Remove'
                        itemLimit={1}
                        onItemSelected={(tag: any) => {
                            const value = tag ? tag.metadata : undefined;
                            _onGoodsChanged(item.id || "", value);
                            return null;
                        }}
                        onChange={(tags) => {
                            if (!tags || tags.length < 1) {
                                _onGoodsChanged(item.id || "", undefined);
                            }
                        }}
                        onResolveSuggestions={_onSearchGoods}
                        onEmptyResolveSuggestions={() => _onSearchGoods('')}
                        disabled={submitting} />
                    {!item.goods ? <Text size={'small'}>Cannot find goods? <Link onClick={() => { setActiveSurface('createGoods') }}>Click here</Link> to create goods.</Text> : null}
                </Stack>
            }
        },
        {
            key: "supplier",
            name: "Supplier",
            minWidth: 140,
            maxWidth: 160,
            onRender: (item: FormDataProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <TextField value={item.supplier || ""}
                        placeholder="Nama Supplier"
                        list="container-goods-supplier-list"
                        disabled={submitting || !item.goods}
                        onChange={(evt, value) => {
                            const _data = data;
                            const _related = _data.find((d) => d.id === item.id);
                            if (_related) {
                                _related.supplier = value || "";
                                setData([..._data]);
                            }
                        }} />
                </Stack>
            }
        },
        {
            key: "quantity",
            name: "Quantity",
            minWidth: 150,
            maxWidth: 150,
            onRender: (item: FormDataProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <TextField value={item.quantityAmount}
                        suffix={item.quantityUnit}
                        disabled={submitting || !item.goods}
                        onChange={(evt, value) => {
                            const _data = data;
                            const _related = _data.find((d) => d.id === item.id);
                            if (_related && ((value || "").trim() === "" || !isNaN(Number(value)))) {
                                _related.quantityAmount = value || "";
                                setData([..._data]);
                            }
                        }} />
                </Stack>
            }
        },
        {
            key: "size",
            name: "Size (length x width x height)",
            minWidth: 300,
            maxWidth: 300,
            onRender: (item: FormDataProps) => {
                return <Stack horizontal tokens={{ childrenGap: 10 }} styles={{ root: { padding: '4px 0px' } }}>
                    <TextField value={item.length}
                        disabled={submitting || !item.goods}
                        suffix={"cm"}
                        onChange={(evt, value) => {
                            const _data = data;
                            const _related = _data.find((d) => d.id === item.id);
                            if (_related && ((value || "").trim() === "" || !isNaN(Number(value)))) {
                                _related.length = value || "";
                                setData([..._data]);
                            }
                        }} />
                    <TextField value={item.width}
                        disabled={submitting || !item.goods}
                        suffix={"cm"}
                        onChange={(evt, value) => {
                            const _data = data;
                            const _related = _data.find((d) => d.id === item.id);
                            if (_related && ((value || "").trim() === "" || !isNaN(Number(value)))) {
                                _related.width = value || "";
                                setData([..._data]);
                            }
                        }} />
                    <TextField value={item.height}
                        disabled={submitting || !item.goods}
                        suffix={"cm"}
                        onChange={(evt, value) => {
                            const _data = data;
                            const _related = _data.find((d) => d.id === item.id);
                            if (_related && ((value || "").trim() === "" || !isNaN(Number(value)))) {
                                _related.height = value || "";
                                setData([..._data]);
                            }
                        }} />
                </Stack>
            }
        },
        {
            key: "volume",
            name: "Volume (m3)",
            minWidth: 100,
            maxWidth: 100,
            onRender: (item: FormDataProps) => {
                return <Stack styles={{ root: { padding: '10px 0px' } }}>
                    <Text>{GeneralService.getNumberWithSeparator(GeneralService.calculateVolume(item.length, item.width, item.height))} m<sup>3</sup></Text>
                </Stack>
            }
        },
        {
            key: "weight",
            name: "Weight (per qty)",
            minWidth: 125,
            maxWidth: 125,
            onRender: (item: FormDataProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <TextField value={item.weightAmount}
                        disabled={submitting || !item.goods}
                        suffix={item.weightUnit}
                        onChange={(evt, value) => {
                            const _data = data;
                            const _related = _data.find((d) => d.id === item.id);
                            if (_related && ((value || "").trim() === "" || !isNaN(Number(value)))) {
                                _related.weightAmount = value || "";
                                setData([..._data]);
                            }
                        }} />
                </Stack>
            }
        },
        {
            key: "totalWeight",
            name: "Total Weight",
            minWidth: 125,
            maxWidth: 125,
            onRender: (item: FormDataProps) => {
                const weight = item.weightAmount !== "" ? Number(item.weightAmount) : 0;
                const quantity = item.quantityAmount !== "" ? Number(item.quantityAmount) : 0;
                const totalWeight = Number((weight * quantity).toFixed(2));

                return <Stack styles={{ root: { padding: '10px 0px' } }}>
                    {item.weightAmount !== "" ? <Text>{GeneralService.getNumberWithSeparator(GeneralService.getWeightInKg(totalWeight, item.weightUnit))} kg</Text> : null}
                    {item.weightAmount === "" ? <Text>0 kg</Text> : null}
                </Stack>
            }
        },
        {
            key: "actions",
            name: "",
            minWidth: 80,
            maxWidth: 80,
            onRender: (item: FormDataProps) => {
                return <Stack.Item styles={{ root: { padding: '16px 0px' } }}>
                    <Stack horizontal className={"detailsListActionRow"} tokens={{ childrenGap: 4 }}>
                        {item.goods ? (
                            <ActionButton
                                className={'detailsListActionButton'}
                                iconProps={{ iconName: "Edit" }}
                                title="Edit Goods"
                                onClick={() => {
                                    if (item.goods?.id) {
                                        setEditingGoodsId(item.goods.id);
                                        setActiveSurface('editGoods');
                                    }
                                }}
                            />
                        ) : null}
                        {(item.goods || data.length > 1) ? (
                            <ActionButton
                                className={'detailsListActionButton'}
                                iconProps={{ iconName: "Delete" }}
                                title="Hapus Baris"
                                onClick={() => {
                                    const _data = data.filter((d) => d.id !== item.id);
                                    const reindexed = _data.map((d, idx) => ({ ...d, orderNumber: idx + 1 }));
                                    setData([...reindexed]);
                                    setIsDirty(true);
                                }}
                            />
                        ) : null}
                    </Stack>
                </Stack.Item>;
            }
        }
    ];

    const _onSearchGoods = async (keyword: string) => {
        try {
            const qs: string[] = [];
            qs.push(`status=all`);
            qs.push(`top=${10}`);
            qs.push(`paged=1`);
            if (keyword && keyword.trim() !== '') { qs.push(`search=${keyword}`) }

            const data = ((await GoodsService.retrieve.get(qs.join("&"))) as any).data.map((item: IGoodsResourceShortProps) => ({
                key: item.id,
                name: item.name,
                metadata: item
            }));

            return data;
        } catch (error) {
            banner.add({
                key: "search_goods_error",
                text: "Failed to search related goods. Error: " + ErrorService.getMessage(error),
                variant: 'error',
                icon: faXmarkCircle
            });

            return [];
        }
    }

    const _onGoodsChanged = (id: string, item?: IGoodsResourceShortProps) => {
        let _data = data;
        const _related = _data.find((g) => g.id === id);
        if (_related) {
            _related.goods = item;
            _related.supplier = item?.supplier || "";
            _related.quantityAmount = item?.quantityAmount ? item.quantityAmount + "" : "";
            _related.quantityUnit = item?.quantityUnit || "";
            _related.length = (item?.length || "") + "";
            _related.width = (item?.width || "") + "";
            _related.height = (item?.height || "") + "";
            _related.weightAmount = item?.weightAmount ? item.weightAmount + "" : "";
            _related.weightUnit = item?.weightUnit || "";
        }

        if (item) {
            const hasEmptyPlaceholder = _data.some((d) => !d.goods);
            if (!hasEmptyPlaceholder) {
                // add placeholder data
                _data.push({
                    id: GeneralService.guid(),
                    supplier: "",
                    quantityAmount: "",
                    quantityUnit: "",
                    length: "",
                    width: "",
                    height: "",
                    weightAmount: "",
                    weightUnit: "",
                    useTotalVolume: false,
                    orderNumber: _data.length + 1
                });
            }
        }

        // sort by order number
        _data = _data.sort((a, b) => a.orderNumber - b.orderNumber);

        setData([..._data]);
    }

    const _onGoodsCreated = (refresh?: boolean, goods?: IGoodsResourceShortProps) => {
        if (goods) {
            const id = (data.find((d) => !d.goods))?.id;
            if (id) { _onGoodsChanged(id, goods); }
        }

        setActiveSurface(undefined);
    }

    const _onGoodsCreatedAnother = (goods: IGoodsResourceShortProps) => {
        const id = (data.find((d) => !d.goods))?.id;
        if (id) { _onGoodsChanged(id, goods); }
    }

    const _onSubmit = async () => {
        try {
            setSubmitting(true);
            const fd = new FormData();
            const filteredGoods = data.filter((d) => d.goods);

            let currentOrder = order;
            if (!currentOrder && props.orderId) {
                try {
                    currentOrder = await OrderService.get(props.orderId);
                    setOrder(currentOrder);
                } catch (err) {
                    console.error("Order fetch error in _onSubmit:", err);
                }
            }

            // Ensure every goods item is safely persisted in the master goods table
            for (const d of filteredGoods) {
                if (d.goods) {
                    let needsCreate = false;
                    try {
                        await GoodsService.get(d.goods.id);
                    } catch (checkErr) {
                        needsCreate = true;
                    }

                    if (needsCreate) {
                        try {
                            const createFd = new FormData();
                            createFd.append('name', (d.goods.name || 'BARANG').toUpperCase());
                            const companyId = d.goods.company?.id || currentOrder?.company?.id;
                            const userId = d.goods.user?.id || (d.goods as any).userId || currentOrder?.customer?.id;
                            if (companyId) createFd.append('companyId', companyId);
                            if (userId) createFd.append('userId', userId);
                            if (d.quantityAmount) createFd.append('quantityAmount', d.quantityAmount);
                            if (d.quantityUnit) createFd.append('quantityUnit', d.quantityUnit);
                            if (d.weightAmount) createFd.append('weightAmount', d.weightAmount);
                            if (d.weightUnit) createFd.append('weightUnit', d.weightUnit);
                            if (d.length) createFd.append('length', d.length);
                            if (d.width) createFd.append('width', d.width);
                            if (d.height) createFd.append('height', d.height);
                            if (d.supplier) createFd.append('supplier', d.supplier.toUpperCase());

                            const created: any = await GoodsService.create(createFd);
                            const realGood = created?.data || created;
                            if (realGood && realGood.id) {
                                d.goods = realGood;
                            }
                        } catch (errCreate) {
                            console.error("Auto persist good before container save failed:", errCreate);
                        }
                    }
                }
            }

            if (filteredGoods.length === 0) {
                fd.append("goods", "[]");
            } else {
                filteredGoods.forEach((d) => {
                    const { goods } = d;
                    const metadata = {
                        ...d,
                        goodsId: goods?.id,
                        supplier: d.supplier !== undefined ? d.supplier : "",
                        deliveryDate: (d as any).deliveryDate !== undefined ? (d as any).deliveryDate : "",
                        length: d.length !== "" ? d.length : 0,
                        width: d.width !== "" ? d.width : 0,
                        height: d.height !== "" ? d.height : 0,
                        quantityAmount: d.quantityAmount !== "" ? d.quantityAmount : 0,
                        weightAmount: d.weightAmount !== "" ? d.weightAmount : 0,
                        containerId: props.container.id,
                        orderNumber: d.orderNumber
                    }
                    fd.append("goods[]", JSON.stringify(metadata));
                });
            }

            await OrderContainerService.update(props.orderId, props.container.id, fd);

            // Save to history snapshot as 'saved'
            if (filteredGoods.length > 0) {
                PackingListStorageService.save({
                    source: 'saved',
                    orderId: props.orderId,
                    orderNumber: order?.orderNumber,
                    containerId: props.container.id,
                    containerNumber: props.container.containerNumber,
                    companyName: order?.company?.name,
                    customerName: order?.customer?.name,
                    goods: filteredGoods
                });
            }

            banner.add({
                key: 'update_container_goods_list_success',
                variant: 'success',
                icon: faCheck,
                text: `Order container goods list updated successfully`
            });
            props.onDismissed(true);
        } catch (e) {
            setSubmitting(false);
            banner.add({
                key: 'update_container_goods_list_error',
                variant: 'error',
                icon: faXmarkCircle,
                text: 'Failed to update order container goods list. Error: ' + ErrorService.getMessage(e),
            });
        }
    }

    const _onApplyHistoryItems = (items: ISavedPackingListItem[], mode: 'replace' | 'append') => {
        if (!items || items.length === 0) return;

        let baseList: FormDataProps[] = [];
        if (mode === 'append') {
            baseList = data.filter(d => d.goods);
        }

        const startOrderNum = baseList.length;

        const newRows: FormDataProps[] = items.map((item, idx) => ({
            id: GeneralService.guid(),
            goods: item.goods,
            supplier: item.supplier || item.goods?.supplier || "",
            deliveryDate: item.deliveryDate || "",
            quantityAmount: item.quantityAmount || (item.goods?.quantityAmount ? String(item.goods.quantityAmount) : "1"),
            quantityUnit: item.quantityUnit || item.goods?.quantityUnit || "CTN",
            length: item.length || (item.goods?.length ? String(item.goods.length) : ""),
            width: item.width || (item.goods?.width ? String(item.goods.width) : ""),
            height: item.height || (item.goods?.height ? String(item.goods.height) : ""),
            weightAmount: item.weightAmount || (item.goods?.weightAmount ? String(item.goods.weightAmount) : ""),
            weightUnit: item.weightUnit || item.goods?.weightUnit || "kg",
            useTotalVolume: item.useTotalVolume || false,
            orderNumber: startOrderNum + idx + 1
        }));

        const combined = [...baseList, ...newRows];
        const reindexed = combined.map((d, idx) => ({ ...d, orderNumber: idx + 1 }));

        // Add empty placeholder row at the end
        reindexed.push({
            id: GeneralService.guid(),
            supplier: "",
            quantityAmount: "",
            quantityUnit: "",
            length: "",
            width: "",
            height: "",
            weightAmount: "",
            weightUnit: "",
            useTotalVolume: false,
            orderNumber: reindexed.length + 1
        });

        setData([...reindexed]);
        setIsDirty(true);
        setHasSavedHistory(false);

        banner.add({
            key: 'apply_packing_list_history_success',
            variant: 'success',
            icon: faCheck,
            text: `${newRows.length} barang berhasil dimuat ke Packing List! Klik 'Save' di bawah untuk menyimpan perubahan ke server.`
        });
    };

    const handleClearAllRows = () => {
        const filled = data.filter(d => d.goods);
        if (filled.length > 0) {
            PackingListStorageService.save({
                source: 'draft',
                orderId: props.orderId,
                orderNumber: order?.orderNumber,
                containerId: props.container.id,
                containerNumber: props.container.containerNumber,
                companyName: order?.company?.name,
                customerName: order?.customer?.name,
                goods: filled
            });
        }

        const emptyRow: FormDataProps = {
            id: GeneralService.guid(),
            supplier: "",
            quantityAmount: "",
            quantityUnit: "",
            length: "",
            width: "",
            height: "",
            weightAmount: "",
            weightUnit: "",
            useTotalVolume: false,
            orderNumber: 1
        };

        setData([emptyRow]);
        setSelectedIds([]);
        setIsDirty(true);
        setShowClearAllDialog(false);

        banner.add({
            key: 'clear_all_container_goods_success',
            variant: 'default',
            text: 'Semua barang telah dikosongkan. Cadangan data telah disimpan di riwayat. Klik "Save" untuk menyimpan perubahan.'
        });
    };

    return <Panel headerText={`Update Container Goods List${container.containerNumber ? " - " + container.containerNumber : ""}`}
        isOpen={true}
        type={PanelType.large}
        onDismiss={() => handleAttemptDismiss(true)}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                {!submitting ? <>
                    <PrimaryButton text={"Save"} onClick={() => { _onSubmit() }} />
                    <DefaultButton text={"Cancel"} onClick={() => { handleAttemptDismiss(false) }} />
                </> : null}
                {submitting ? <LoadingComponent label='Saving goods list ...' labelPosition='right' spinnerPosition='baseline' /> : null}
            </Stack>;
        }}>
        <Stack tokens={{ childrenGap: 10 }}>
            <datalist id="container-goods-supplier-list">
                {supplierSuggestions.map((supName, idx) => (
                    <option key={idx} value={supName} />
                ))}
            </datalist>

            {/* Smart History Notification Banner (Like Offers form) */}
            {hasSavedHistory && savedHistoryData && (
                <MessageBar
                    messageBarType={MessageBarType.success}
                    isMultiline={true}
                    styles={{
                        root: {
                            backgroundColor: '#f0fdf4',
                            border: '1px solid #86efac',
                            borderRadius: '8px',
                            padding: '6px 12px',
                            color: '#166534',
                        }
                    }}
                    actions={
                        <Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign="center">
                            <PrimaryButton
                                text="Pilih dari Riwayat..."
                                iconProps={{ iconName: 'History' }}
                                onClick={() => setIsHistoryModalOpen(true)}
                                styles={{
                                    root: { height: 32, padding: '0 12px', backgroundColor: '#16a34a', borderColor: '#16a34a' },
                                    rootHovered: { backgroundColor: '#15803d', borderColor: '#15803d' }
                                }}
                            />
                            <DefaultButton
                                text={`Gunakan Data Terakhir (${savedHistoryData.itemCount} Barang)`}
                                iconProps={{ iconName: 'Copy' }}
                                onClick={() => _onApplyHistoryItems(savedHistoryData.goods, 'replace')}
                                styles={{
                                    root: { height: 32, padding: '0 12px', borderColor: '#16a34a', color: '#15803d' },
                                    rootHovered: { backgroundColor: '#dcfce7', color: '#166534' }
                                }}
                            />
                            <IconButton
                                iconProps={{ iconName: 'Cancel' }}
                                title="Tutup pemberitahuan ini"
                                onClick={() => setHasSavedHistory(false)}
                                styles={{ root: { color: '#64748b' } }}
                            />
                        </Stack>
                    }
                >
                    <Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign="center">
                        <Text size={'small'} style={{ color: '#166534' }}>
                            <strong>Ditemukan riwayat pengisian packing list sebelumnya</strong>
                            {savedHistoryData.orderNumber ? ` dari Order #${savedHistoryData.orderNumber}` : ''}
                            {savedHistoryData.containerNumber ? ` (${savedHistoryData.containerNumber})` : ''}
                            {` berisi ${savedHistoryData.itemCount} macam barang.`}
                            {" Anda dapat menyalin data ini ke kontainer saat ini atau memilih riwayat lain."}
                        </Text>
                    </Stack>
                </MessageBar>
            )}

            {/* Quick Multi-Row Add Toolbar */}
            <Stack
                horizontal
                wrap
                verticalAlign="center"
                horizontalAlign="space-between"
                styles={{
                    root: {
                        backgroundColor: '#f8fafc',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        marginBottom: '4px'
                    }
                }}
            >
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                    <Text style={{ fontWeight: 600, color: '#334155', fontSize: '13px' }}>
                        Total: {data.filter(d => d.goods).length} barang terisi ({data.length} baris)
                    </Text>
                </Stack>

                <Stack horizontal wrap verticalAlign="center" tokens={{ childrenGap: 8 }}>
                    <PrimaryButton
                        text="Scan Tally Sheet (AI)"
                        iconProps={{ iconName: 'AutoEnhance' }}
                        disabled={submitting}
                        onClick={() => setIsTallyScanOpen(true)}
                    />
                    <PrimaryButton
                        text="Scan Surat Jalan (AI)"
                        iconProps={{ iconName: 'DocumentSearch' }}
                        disabled={submitting}
                        onClick={() => setIsSuratJalanScanOpen(true)}
                        styles={{
                            root: { backgroundColor: '#0d9488', borderColor: '#0d9488' },
                            rootHovered: { backgroundColor: '#0f766e', borderColor: '#0f766e' }
                        }}
                    />
                    <DefaultButton
                        text="Riwayat & Salin Barang"
                        iconProps={{ iconName: 'History' }}
                        disabled={submitting}
                        onClick={() => setIsHistoryModalOpen(true)}
                        styles={{
                            root: { borderColor: '#2563eb', color: '#1d4ed8' },
                            rootHovered: { backgroundColor: '#eff6ff', color: '#1e40af' }
                        }}
                    />
                    <Text style={{ color: '#64748b', fontSize: '12px', fontWeight: 500, marginLeft: '6px', marginRight: '6px' }}>
                        Tambah Baris Kosong:
                    </Text>
                    <TextField
                        type="number"
                        value={rowCountInput}
                        onChange={(evt, value) => {
                            if (value === '' || (!isNaN(Number(value)) && Number(value) >= 1)) {
                                setRowCountInput(value || '');
                            }
                        }}
                        styles={{ root: { width: 65 } }}
                        disabled={submitting}
                    />
                    <DefaultButton
                        text="Tambah"
                        iconProps={{ iconName: 'Plus' }}
                        disabled={submitting || !rowCountInput || Number(rowCountInput) <= 0}
                        onClick={() => handleAddMultipleRows(Number(rowCountInput) || 1)}
                    />
                    <DefaultButton
                        text="+5 Baris"
                        disabled={submitting}
                        onClick={() => handleAddMultipleRows(5)}
                    />
                    <DefaultButton
                        text="+10 Baris"
                        disabled={submitting}
                        onClick={() => handleAddMultipleRows(10)}
                    />

                    {data.some(d => !d.goods) && data.length > 1 && (
                        <DefaultButton
                            text="Bersihkan Baris Kosong"
                            iconProps={{ iconName: 'Clear' }}
                            disabled={submitting}
                            onClick={handleClearEmptyRows}
                        />
                    )}

                    {data.some(d => d.goods) && (
                        <DefaultButton
                            text="Kosongkan Semua Barang"
                            iconProps={{ iconName: 'Delete' }}
                            disabled={submitting}
                            onClick={() => setShowClearAllDialog(true)}
                            styles={{
                                root: {
                                    color: '#b91c1c',
                                    borderColor: '#fca5a5'
                                },
                                rootHovered: {
                                    backgroundColor: '#fee2e2',
                                    color: '#991b1b'
                                }
                            }}
                        />
                    )}

                    {selectedIds.length > 0 && (
                        <DefaultButton
                            text={`Hapus ${selectedIds.length} Baris Terpilih`}
                            iconProps={{ iconName: 'Delete' }}
                            disabled={submitting}
                            onClick={handleBulkDelete}
                            styles={{
                                root: {
                                    backgroundColor: '#fee2e2',
                                    color: '#dc2626',
                                    borderColor: '#fca5a5',
                                    fontWeight: 600
                                },
                                rootHovered: {
                                    backgroundColor: '#fecaca',
                                    color: '#b91c1c',
                                    borderColor: '#f87171'
                                }
                            }}
                        />
                    )}
                </Stack>
            </Stack>

            <ShimmeredDetailsList
                setKey="items"
                items={data}
                columns={columns}
                selectionMode={SelectionMode.none}
                enableShimmer={false}
                onShouldVirtualize={() => false}
                ariaLabelForShimmer="Content is being fetched"
                ariaLabelForGrid="Item details" />
        </Stack>
        {activeSurface === 'createGoods' ? <GoodsForm onDismissed={_onGoodsCreated} onGoodsCreated={_onGoodsCreatedAnother} defaultCompany={order?.company} defaultCustomer={order?.customer} /> : null}
        {activeSurface === 'editGoods' && editingGoodsId ? <GoodsForm goodsId={editingGoodsId} onDismissed={(refresh, updatedGoods) => {
            if (updatedGoods) {
                const _data = data.map((d) => {
                    if (d.goods?.id === updatedGoods.id || d.goods?.id === editingGoodsId) {
                        return {
                            ...d,
                            goods: updatedGoods,
                            supplier: updatedGoods.supplier !== undefined && updatedGoods.supplier !== null ? updatedGoods.supplier : (d.supplier || ""),
                            quantityAmount: updatedGoods.quantityAmount !== undefined && updatedGoods.quantityAmount !== null ? String(updatedGoods.quantityAmount) : d.quantityAmount,
                            quantityUnit: updatedGoods.quantityUnit || d.quantityUnit || "",
                            weightAmount: updatedGoods.weightAmount !== undefined && updatedGoods.weightAmount !== null ? String(updatedGoods.weightAmount) : d.weightAmount,
                            weightUnit: updatedGoods.weightUnit || d.weightUnit || "",
                            length: updatedGoods.length !== undefined && updatedGoods.length !== null ? String(updatedGoods.length) : d.length,
                            width: updatedGoods.width !== undefined && updatedGoods.width !== null ? String(updatedGoods.width) : d.width,
                            height: updatedGoods.height !== undefined && updatedGoods.height !== null ? String(updatedGoods.height) : d.height,
                            deliveryDate: updatedGoods.deliveryDate !== undefined ? updatedGoods.deliveryDate : (d as any).deliveryDate
                        };
                    }
                    return d;
                });
                setData([..._data]);
                setIsDirty(true);
            }
            setEditingGoodsId(undefined);
            setActiveSurface(undefined);
        }} defaultCompany={order?.company} defaultCustomer={order?.customer} /> : null}

        <TallySheetScanModal
            isOpen={isTallyScanOpen}
            onDismiss={() => setIsTallyScanOpen(false)}
            onApply={(items, containerInfo) => handleApplyTallyItems(items, containerInfo)}
            defaultCompany={order?.company}
            defaultCustomer={order?.customer}
        />

        <SuratJalanGoodsScanModal
            isOpen={isSuratJalanScanOpen}
            onDismiss={() => setIsSuratJalanScanOpen(false)}
            onApply={(items, suratJalanInfo) => handleApplySuratJalanItems(items, suratJalanInfo)}
            defaultCompany={order?.company}
            defaultCustomer={order?.customer}
        />

        <PackingListHistoryModal
            isOpen={isHistoryModalOpen}
            onDismiss={() => setIsHistoryModalOpen(false)}
            onApply={(items, mode) => _onApplyHistoryItems(items, mode)}
            onHistoryListChanged={() => {
                setSavedHistoryData(PackingListStorageService.getLatest());
            }}
            currentOrderId={props.orderId}
            currentContainerId={props.container.id}
            currentGoodsCount={data.filter(d => d.goods).length}
        />

        {showClearAllDialog && (
            <Dialog
                hidden={false}
                onDismiss={() => setShowClearAllDialog(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Kosongkan Semua Barang?',
                    subText: `Apakah Anda yakin ingin mengosongkan ${data.filter(d => d.goods).length} barang dari kontainer ini? Salinan cadangan barang ini akan otomatis disimpan di Riwayat Packing List sehingga dapat dipulihkan sewaktu-waktu.`
                }}
            >
                <DialogFooter>
                    <PrimaryButton
                        text="Ya, Kosongkan Semua"
                        styles={{ root: { backgroundColor: '#dc2626', borderColor: '#dc2626' } }}
                        onClick={handleClearAllRows}
                    />
                    <DefaultButton
                        text="Batal"
                        onClick={() => setShowClearAllDialog(false)}
                    />
                </DialogFooter>
            </Dialog>
        )}

        {showUnsavedDialog && (
            <Dialog
                hidden={false}
                onDismiss={() => setShowUnsavedDialog(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Perubahan Belum Disimpan!',
                    subText: 'Terdapat perubahan data Packing List (seperti menghapus atau menambah barang) yang belum disimpan ke server. Simpan sekarang?'
                }}
            >
                <DialogFooter>
                    <PrimaryButton
                        text="Simpan & Keluar"
                        onClick={async () => {
                            setShowUnsavedDialog(false);
                            await _onSubmit();
                        }}
                    />
                    <DefaultButton
                        text="Keluar Tanpa Menyimpan"
                        styles={{ root: { color: '#dc2626', borderColor: '#fca5a5' } }}
                        onClick={() => {
                            setShowUnsavedDialog(false);
                            props.onDismissed(false);
                        }}
                    />
                    <DefaultButton
                        text="Batal"
                        onClick={() => setShowUnsavedDialog(false)}
                    />
                </DialogFooter>
            </Dialog>
        )}
    </Panel>
};

export default OrderContainerGoodsForm;